"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Clock, Phone, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const processHighlights = [{
  step: "01",
  title: "Direct call",
  desc: "Speak directly with local dispatch, not an automated call center."
}, {
  step: "02",
  title: "Rapid dispatch",
  desc: "A field crew is sent immediately to contain damage on site."
}, {
  step: "03",
  title: "Insurance handled",
  desc: "Complete itemization, photo logs, and claims billing handled directly."
}];
export default function ProcessHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        ease: "easeOut"
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="processhero" data-nav="dark" className="relative overflow-hidden bg-background text-foreground">
      {/* Dark hero atmosphere overlay */}
      <div className="absolute inset-0 bg-secondary text-secondary-foreground" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80 pointer-events-none" />

      {/* Decorative subtle texture/ambient lighting */}
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-6xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="max-w-3xl">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="mb-6 flex items-center gap-2">
            <Badge variant="outline" className="border-primary-foreground/30 bg-primary/20 text-primary-foreground text-xs font-medium uppercase tracking-wider py-1 px-3">
              <Clock className="size-3.5 mr-1" />
              24/7 Rapid Response
            </Badge>
            <span className="text-xs text-primary-foreground/80 hidden sm:inline-flex items-center gap-1">
              <ShieldCheck className="size-3.5 text-accent" />
              Serving MD, DC &amp; Northern VA
            </span>
          </motion.div>

          {/* Headline & Subheadline */}
          <motion.h1 variants={itemVariants} className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl font-default">
            Our process
          </motion.h1>

          <motion.p variants={itemVariants} className="mt-6 text-xl text-primary-foreground/90 sm:text-2xl font-light leading-relaxed">
            What happens after you call TriServe.
          </motion.p>

          <motion.p variants={itemVariants} className="mt-4 text-base text-primary-foreground/75 leading-relaxed max-w-2xl">
            When water, fire, or mold threatens your property, you need immediate stabilization without call center delays. Our local crews handle every step, from on-site emergency containment to complete claims coordination with your insurance adjuster.
          </motion.p>

          {/* Action buttons */}
          <motion.div variants={itemVariants} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium shadow-md">
              <Link to="/ContactRequestServicePage">
                Request emergency service
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-white hover:bg-white/10">
              <a href="tel:8005550199">
                <Phone className="size-4 mr-2" />
                (800) 555-0199
              </a>
            </Button>
          </motion.div>
        </motion.div>

        {/* Step Preview Strip */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="mt-16 grid grid-cols-1 gap-4 border-t border-primary-foreground/15 pt-8 sm:grid-cols-3">
          {processHighlights.map((item, index) => <motion.div key={item.step} variants={itemVariants} data-index={index} className="rounded-lg border border-primary-foreground/10 bg-white/5 p-5 backdrop-blur-xs">
              <span className="text-xs font-bold text-accent tracking-widest block mb-1 font-mono">
                {item.step}
              </span>
              <h2 className="text-base font-semibold text-white font-default mb-1">
                {item.title}
              </h2>
              <p className="text-sm text-primary-foreground/70 leading-normal">
                {item.desc}
              </p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}